import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, BookOpen } from 'lucide-react';
const tomosData = [{
  title: 'TOMO 1 — Fundamentos del Docente de Telas',
  subtitle: 'De alumna a profe: todo lo que necesitás saber antes de dar tu primera clase',
  content: 'Mentalidad docente, anatomía básica aplicada a telas, tipos de telas y montaje seguro, normas de seguridad esenciales, cómo leer el cuerpo del alumno, errores más comunes del docente principiante y checklist completo del profe listo para enseñar.',
  color: 'blue',
  icon: '📖',
  badge: 'Teoría esencial'
}, {
  title: 'TOMO 2 — Planificación de Clases Nivel Básico',
  subtitle: 'Cómo armar clases completas, progresivas y seguras desde cero',
  content: 'Estructura de una clase de telas completa, progresión didáctica del suelo al aire, 15 clases listas para usar, cómo adaptar la clase según el grupo y plantilla editable de planificación semanal.',
  color: 'green',
  icon: '🟢',
  badge: '15 clases listas'
}, {
  title: 'TOMO 3 — Planificación de Clases Nivel Intermedio',
  subtitle: 'Elevá tus clases: figuras más complejas con metodología clara',
  content: 'Criterios para pasar un alumno de nivel, descripción técnica de figuras intermedias, 15 clases completas, cómo combinar figuras en secuencias, corrección postural y verbal efectiva y registro de progreso del alumno.',
  color: 'yellow',
  icon: '🟡',
  badge: '15 clases listas'
}, {
  title: 'TOMO 4 — Planificación de Clases Nivel Avanzado',
  subtitle: 'Diseñá experiencias de alto vuelo para alumnos exigentes',
  content: 'El alumno avanzado y sus necesidades, figuras avanzadas y combinaciones coreográficas, 15 clases completas, introducción a la coreografía en telas, trabajo de fuerza y flexibilidad específica y rúbrica de evaluación de nivel.',
  color: 'red',
  icon: '🔴',
  badge: '15 clases listas'
}, {
  title: 'TOMO 5 — Gestión y Organización del Docente',
  subtitle: 'Administrá tu espacio, tus alumnos y tu tiempo como una profe profesional',
  content: 'Sistema de niveles y grupos, fichas de inscripción y consentimiento informado, cómo cobrar y fijar precios, cómo manejar alumnos con lesiones, organización mensual de contenidos y kit de formularios listos para usar.',
  color: 'purple',
  icon: '💼',
  badge: 'Plantillas incluidas'
}, {
  title: 'TOMO 6 — Marketing para Profe de Telas',
  subtitle: 'Llenate de alumnos sin depender del boca a boca',
  content: 'Marca personal como profe de telas, qué publicar en Instagram para atraer alumnos, cómo usar reels y stories, estrategias para conseguir tus primeros 20 alumnos, cómo fidelizar y reducir la rotación y calendario de contenido mensual.',
  color: 'pink',
  icon: '📱',
  badge: 'Plan de 30 días'
}];
const colorStyles = {
  blue: {
    border: 'border-l-blue-500',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-800'
  },
  green: {
    border: 'border-l-green-500',
    bg: 'bg-green-50',
    text: 'text-green-700',
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-800'
  },
  yellow: {
    border: 'border-l-yellow-500',
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    badgeBg: 'bg-yellow-100',
    badgeText: 'text-yellow-800'
  },
  red: {
    border: 'border-l-red-500',
    bg: 'bg-red-50',
    text: 'text-red-700',
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-800'
  },
  purple: {
    border: 'border-l-purple-500',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-800'
  },
  pink: {
    border: 'border-l-pink-500',
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    badgeBg: 'bg-pink-100',
    badgeText: 'text-pink-800'
  }
};
const includedFeatures = ['Planificación lista para usar desde el primer día', 'Clases con objetivo, desarrollo y cierre', 'Indicaciones técnicas listas para usar en clase', 'Variaciones Nivel 1, Nivel 2 y Nivel 3 por figura', 'Checklist pre y durante la clase', 'Planillas de seguimiento de alumnas', 'Adaptaciones para grupos heterogéneos', '100% adaptado a la realidad del docente de telas argentino'];
export const TomosSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-10 md:py-20 bg-gray-50" data-id="element-450">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-451">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-6 md:mb-12" data-id="element-452">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 text-primary mb-3 md:mb-6" data-id="element-453">
            <BookOpen size={24} className="md:hidden" data-id="element-454" />
            <BookOpen size={32} className="hidden md:block" data-id="element-455" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold font-serif text-gray-900 mb-2 md:mb-4" data-id="element-456">
            Los 6 Tomos
          </h2>
          <p className="text-base md:text-xl text-gray-600 font-medium" data-id="element-457">
            Para profes de telas de todos los niveles y enfoques
          </p>
        </motion.div>

        <div className="space-y-3 md:space-y-4 max-w-3xl mx-auto" data-id="element-458">
          {tomosData.map((tomo, index) => {
          const isOpen = openIndex === index;
          const styles = colorStyles[tomo.color as keyof typeof colorStyles];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className={`border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300 border-l-4 ${styles.border}`} data-id="element-459">
                <button className="w-full text-left px-4 py-3 md:px-6 md:py-5 flex items-center justify-between focus:outline-none" onClick={() => setOpenIndex(isOpen ? null : index)} data-id="element-460">
                  <div className="flex items-center gap-3 md:gap-4 pr-2 md:pr-4 w-full" data-id="element-461">
                    <span className="text-2xl md:text-3xl flex-shrink-0" data-id="element-462">
                      {tomo.icon}
                    </span>
                    <div className="flex-grow" data-id="element-463">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1" data-id="element-464">
                        <h3 className="font-serif text-base md:text-xl font-bold text-gray-900" data-id="element-465">
                          {tomo.title}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] md:text-xs font-bold uppercase tracking-wide w-fit ${styles.badgeBg} ${styles.badgeText}`} data-id="element-466">
                          {tomo.badge}
                        </span>
                      </div>
                      <p className="text-gray-500 font-medium text-xs md:text-sm" data-id="element-467">
                        {tomo.subtitle}
                      </p>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className={`flex-shrink-0 ${isOpen ? styles.text : 'text-gray-400'}`} data-id="element-468">
                    <ChevronDown size={20} className="md:hidden" data-id="element-469" />
                    <ChevronDown size={24} className="hidden md:block" data-id="element-470" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-471">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-472">
                      <div className={`px-4 pb-4 md:px-6 md:pb-6 pt-2 text-sm md:text-base text-gray-700 leading-relaxed border-t border-gray-100 ${styles.bg}`} data-id="element-473">
                        {tomo.content}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-8 md:mt-16 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl md:rounded-2xl p-6 md:p-10 border border-orange-100 shadow-lg relative overflow-hidden" data-id="element-474">
          {/* Decorative accent */}
          <div className="absolute top-0 left-0 w-1.5 h-full bg-primary" data-id="element-475"></div>

          <h3 className="text-xl md:text-2xl font-bold font-serif text-primary mb-4 md:mb-8 text-center" data-id="element-476">
            Cada tomo incluye:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5" data-id="element-477">
            {includedFeatures.map((feature, index) => <div key={index} className="flex items-start gap-2 md:gap-3 bg-white/60 p-3 rounded-lg border border-orange-50/50" data-id="element-478">
                <CheckCircle2 className="text-accent flex-shrink-0 mt-0.5 w-4 h-4 md:w-5 md:h-5" data-id="element-479" />
                <span className="text-sm md:text-base text-gray-800 font-medium" data-id="element-480">
                  {feature}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
};